import React, {
  ChangeEvent,
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ButtonComponent } from '../../../../ReusableComponents/Button/ButtonComponent';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import { StoreCapacity } from '../../../../types/capacityTypes';
import { RiFileDownloadLine, RiFilter2Line } from 'react-icons/ri';
import { FiEdit3 } from 'react-icons/fi';
import { FaSortAmountDownAlt, FaSortAmountDown } from 'react-icons/fa';
import { useDeepCompareCallback, useDeepCompareEffect } from 'use-deep-compare';
import { TooltipComponent } from '../../../../ReusableComponents/Tooltip/TooltipComponent';
import TablePagination from '@material-ui/core/TablePagination';
import { EditStoreConfigModal } from './EditStoreCapacityModal/EditStoreConfigModal';
import { MdSearch } from 'react-icons/md';
import { InputComponent } from '../../../../ReusableComponents/Input/InputComponent';
import _ from 'lodash';
import dayjs from 'dayjs';
import ModalComponent from '../../../../ReusableComponents/Modal/ModalComponent';
import { capitalize } from '../../../../utils/strings';
import { Alert, Skeleton } from '@material-ui/lab';
import { Snackbar } from '@material-ui/core';
import './CapacityStoreSection.scss';
import { CapacityStoreFilterPopover } from './CapacityStoreFilterPopover/CapacityStoreFilterPopover';

const defaultCutoffTimeRange = [0, 23];
interface ToastConfigInterface {
  text: string;
  type: 'success' | 'error';
}

interface Props {
  storesCapacity?: StoreCapacity[];
  updateStoresCapacity: () => void;
  allowEditing: boolean;
}

interface SortingObject {
  sortKey: string;
  sortType: '' | 'asc' | 'desc';
}

const sortForCSV = [
  'name',
  'cutOff',
  'openForPickup',
  'todaysOrderCount',
  'maxOrdersForDate',
];

enum TableHeaders {
  stores = 'Stores',
  active = 'Active',
  cutOff = 'Cut off times',
  todaysOrderCount = 'Daily orders',
  maxOrdersForDate = 'Max daily orders',
}

type TableHeadersTypes = keyof typeof TableHeaders;

function sortMethodForTableColumn(
  a: StoreCapacity,
  b: StoreCapacity,
  sortByFromStoresCapacity: string,
  someZonesOpen: (store: StoreCapacity) => boolean,
  formatCutOff: (store: StoreCapacity) => string
) {
  if (sortByFromStoresCapacity === 'active') {
    return +someZonesOpen(b) - +someZonesOpen(a);
  } else if (sortByFromStoresCapacity === 'cutOff') {
    return formatCutOff(a).localeCompare(formatCutOff(b));
  } else if (
    sortByFromStoresCapacity === 'maxOrdersForDate' ||
    sortByFromStoresCapacity === 'todaysOrderCount'
  ) {
    return (
      (a[sortByFromStoresCapacity] || 0) - (b[sortByFromStoresCapacity] || 0)
    );
  } else {
    return a.name.localeCompare(b.name);
  }
}

const CapacityStoreSection = memo((props: Props) => {
  const { storesCapacity, updateStoresCapacity, allowEditing } = props;
  const filterButtonRef = useRef<HTMLButtonElement>();
  const didMountRef = useRef<boolean>(false);
  const [showLarge, setShowLarge] = useState(false);
  const [showDelivery, setShowDelivery] = useState(true);
  const [showSpecial, setShowSpecial] = useState(false);
  const [isPopoverVisible, togglePopover] = useState<boolean>(false);
  const [filterRadioCapacity, setFilterRadioCapacity] = useState<string>('');
  const [filterRadioActiveInactiveStores, setFilterRadioActiveInactiveStores] =
    useState<string>('');
  const [cutoffTimeRange, setCutoffTimeRange] = useState<number | number[]>(
    defaultCutoffTimeRange
  );
  const [cutoffTimeVisualRange, setCutoffTimeVisualRange] = useState<
    number | number[]
  >([0, 23]);
  const [isToastMessageVisible, toggleToastMessage] = useState<boolean>(false);
  const [isSkeletonLoaderVisible, toggleSkeletonLoader] =
    useState<boolean>(false);
  const [toastConfigObject, setToastConfigObject] =
    useState<ToastConfigInterface>({ text: '', type: 'error' });
  const [sortObject, setSortObject] = useState<SortingObject>({
    sortKey: '',
    sortType: '',
  });
  const [selectedStore, setSelectedStore] = useState<StoreCapacity | undefined>(
    undefined
  );
  const [filteredSortedStores, setFilteredSortedStores] = useState<
    StoreCapacity[] | undefined
  >(undefined);
  const [page, setPage] = useState(0);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [rowsPerPage, setRowsPerPage] = useState(24);
  const [isEditStoreConfigModalVisible, toggleEditStoreModal] =
    useState<boolean>(false);

  const modalToggleDeliverySection = () => {
    setShowDelivery(!showDelivery);
    setShowLarge(false);
    setShowSpecial(false);
  };

  const modalToggleLargeSection = () => {
    setShowDelivery(false);
    setShowLarge(!showLarge);
    setShowSpecial(false);
  };

  const modalToggleSpecialSection = () => {
    setShowDelivery(false);
    setShowLarge(false);
    setShowSpecial(!showSpecial);
  };

  const setFilterRadioCapacityCallback = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setFilterRadioCapacity(event.target.value),
    []
  );
  const setFilterRadioActiveInactiveStoresCallback = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setFilterRadioActiveInactiveStores(event.target.value),
    []
  );
  const setCutoffVisualChangeCallback = useCallback(
    (
      event: ChangeEvent<Record<string, unknown>>,
      newValue: number | number[]
    ) => {
      setCutoffTimeVisualRange(newValue);
    },
    []
  );
  const setCutoffTimeRangeCallback = useCallback(
    (
      event: ChangeEvent<Record<string, unknown>>,
      newValue: number | number[]
    ) => setCutoffTimeRange(newValue),
    []
  );
  const timeRangeValueText = useCallback((value: number) => {
    if (value.toString().length === 1) {
      return `0${value}:00`;
    } else {
      return `${value}:00`;
    }
  }, []);
  const clearAllFilter = useCallback(() => {
    filterRadioActiveInactiveStores && setFilterRadioActiveInactiveStores('');
    filterRadioCapacity && setFilterRadioCapacity('');
    !_.isEqual(cutoffTimeRange, defaultCutoffTimeRange) &&
      setCutoffTimeRange(defaultCutoffTimeRange);
    !_.isEqual(cutoffTimeVisualRange, defaultCutoffTimeRange) &&
      setCutoffTimeVisualRange(defaultCutoffTimeRange);
  }, [
    cutoffTimeRange,
    cutoffTimeVisualRange,
    filterRadioActiveInactiveStores,
    filterRadioCapacity,
  ]);
  const memoInputAdornment = useMemo(
    () => ({ adornment: <MdSearch />, position: 'end' as const }),
    []
  );
  const editIcon = useMemo((): JSX.Element => <FiEdit3 />, []);
  const csvDownloadIcon = useMemo(
    (): JSX.Element => <RiFileDownloadLine />,
    []
  );
  const filterIcon = useMemo((): JSX.Element => <RiFilter2Line />, []);
  const handleChangePage = (event: unknown, newPage: number): void =>
    setPage(newPage);
  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  const formatTime = (time: string): string => time.substr(0, 5);
  const formatCutOff = useCallback(
    (store: StoreCapacity, defaultValue = '—'): string =>
      store.cutOff ? formatTime(store.cutOff) : defaultValue,
    []
  );
  const qbbCutoffAdjustmentMinutes = (
    store: StoreCapacity,
    defaultValue = '—'
  ): string =>
    store.qbbCutoff ? formatTime(store.qbbCutoff.toString()) : defaultValue;
  const someZonesOpen = (store: StoreCapacity): boolean =>
    store.openForPickup.some(zoneEntry => zoneEntry.pickupOpen);
  const someButNotAllZonesOpen = (store: StoreCapacity): boolean =>
    someZonesOpen(store) &&
    store.openForPickup.some(zoneEntry => !zoneEntry.pickupOpen);
  const pickupToolTipData = (store: StoreCapacity): string =>
    store.openForPickup
      .map(
        zoneEntry =>
          `${zoneEntry.zone}: ${zoneEntry.pickupOpen ? 'open' : 'closed'}`
      )
      .join(', ');
  const sortingByKeys = (
    sortByFromStoresCapacity: string,
    sortingObjectFromParams: SortingObject,
    dataToSort: StoreCapacity[]
  ): StoreCapacity[] | undefined => {
    const sortedCapacityData = [...dataToSort].sort((a, b) =>
      sortMethodForTableColumn(
        a,
        b,
        sortByFromStoresCapacity,
        someZonesOpen,
        formatCutOff
      )
    );
    if (sortingObjectFromParams.sortType === 'desc') {
      return sortedCapacityData.reverse();
    }
    return sortedCapacityData;
  };

  const debouncedFilterAction = useDeepCompareCallback(
    _.debounce(
      (
        inputValue: string,
        sortObjectParam: SortingObject = sortObject
      ): void => {
        let newFilteredStoresValue: StoreCapacity[] | undefined = [
          ...storesCapacity,
        ];
        if (inputValue) {
          newFilteredStoresValue = newFilteredStoresValue.filter(
            store =>
              store &&
              store.name &&
              store.name.toLowerCase().includes(inputValue.toLowerCase())
          );
        }
        if (filterRadioCapacity) {
          newFilteredStoresValue = newFilteredStoresValue.filter(store => {
            if (filterRadioCapacity === 'fullCapacity') {
              return store?.maxOrdersForDate === +store.todaysOrderCount;
            } else {
              return store?.maxOrdersForDate !== +store.todaysOrderCount;
            }
          });
        }
        if (filterRadioActiveInactiveStores) {
          newFilteredStoresValue = newFilteredStoresValue.filter(store => {
            if (filterRadioActiveInactiveStores === 'active') {
              return someZonesOpen(store);
            } else {
              return !someZonesOpen(store);
            }
          });
        }
        if (!_.isEqual(cutoffTimeRange, defaultCutoffTimeRange)) {
          newFilteredStoresValue = newFilteredStoresValue.filter(store => {
            if (store.cutOff && Array.isArray(cutoffTimeRange)) {
              const formattedTimeFrom = timeRangeValueText(cutoffTimeRange[0]);
              const formattedTimeTo = timeRangeValueText(cutoffTimeRange[1]);
              return (
                store.cutOff >= formattedTimeFrom &&
                store.cutOff <= formattedTimeTo
              );
            }
            return false;
          });
        }
        switch (sortObjectParam.sortKey) {
          case 'stores':
            newFilteredStoresValue = sortingByKeys(
              'name',
              sortObjectParam,
              newFilteredStoresValue
            );
            break;
          case 'cutOff':
            newFilteredStoresValue = sortingByKeys(
              'cutOff',
              sortObjectParam,
              newFilteredStoresValue
            );
            break;
          case 'active':
            newFilteredStoresValue = sortingByKeys(
              'active',
              sortObjectParam,
              newFilteredStoresValue
            );
            break;
          case 'todaysOrderCount':
            newFilteredStoresValue = sortingByKeys(
              'todaysOrderCount',
              sortObjectParam,
              newFilteredStoresValue
            );
            break;
          case 'maxOrdersForDate':
            newFilteredStoresValue = sortingByKeys(
              'maxOrdersForDate',
              sortObjectParam,
              newFilteredStoresValue
            );
            break;
          default:
            newFilteredStoresValue = sortingByKeys(
              '',
              sortObjectParam,
              newFilteredStoresValue
            );
        }
        if (!_.isEqual(filteredSortedStores, newFilteredStoresValue)) {
          setFilteredSortedStores(newFilteredStoresValue);
        }
      },
      500
    ),
    [
      sortObject,
      filteredSortedStores,
      filterRadioActiveInactiveStores,
      filterRadioCapacity,
      cutoffTimeRange,
    ]
  );
  const setActiveSorting = (event: MouseEvent<HTMLDivElement>): void => {
    const { key } = event.currentTarget.dataset;
    const sortObjectForParam: SortingObject = { sortKey: '', sortType: '' };
    if (sortObject.sortKey === key && sortObject.sortType === 'asc') {
      sortObjectForParam.sortKey = sortObject.sortKey;
      sortObjectForParam.sortType = 'desc';
    } else if (!sortObject.sortKey || sortObject.sortKey !== key) {
      sortObjectForParam.sortKey = key as string;
      sortObjectForParam.sortType = 'asc';
    }
    !isSkeletonLoaderVisible && toggleSkeletonLoader(true);
    debouncedFilterAction(searchInputValue, sortObjectForParam);
    setSortObject(sortObjectForParam);
  };
  const onSearchInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      setSearchInputValue(value);
      debouncedFilterAction(value);
    },
    [debouncedFilterAction]
  );
  const triggerStoreEdit = useDeepCompareCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      const clickedStore = e.currentTarget.value;
      const storeToEdit = storesCapacity?.find(
        store => store.name === clickedStore
      );
      setSelectedStore(storeToEdit);
      toggleEditStoreModal(true);
    },
    [storesCapacity]
  );
  const prepareAndDownloadCSV = useCallback((): void => {
    const csvContent = `data:text/csv;charset=utf-8,Store,Cutoff,Active,CurrentDailyOrders,DailyMaxCapacity\n${storesCapacity
      ?.map(store =>
        Object.keys(store)
          .filter(
            key =>
              key !== 'pickupDays' &&
              key !== 'accountId' &&
              key !== 'displayBasePriceCents'
          )
          .sort((a, b) => sortForCSV.indexOf(a) - sortForCSV.indexOf(b))
          .map(key => {
            if (key === 'openForPickup') {
              return someZonesOpen(store) ? 'open' : 'close';
            } else {
              return store[key as keyof StoreCapacity];
            }
          })
          .join(',')
      )
      .join('\n')}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `stores_capacity_${dayjs().format('DD-MM-YYYY')}.csv`
    );
    document.body.appendChild(link); // Required for FF
    link.click();
  }, [storesCapacity]);
  const onCloseModal = useCallback((): void => {
    toggleEditStoreModal(false);
    setSelectedStore(undefined);
  }, []);
  const onCloseToast = useCallback((): void => toggleToastMessage(false), []);
  const onOpenPopover = useCallback((): void => togglePopover(true), []);
  const onClosePopover = useCallback((): void => togglePopover(false), []);
  useEffect((): void => {
    if (isSkeletonLoaderVisible && didMountRef.current) {
      setTimeout((): void => toggleSkeletonLoader(false), 1000);
    }
  });
  useDeepCompareEffect((): void => {
    if (didMountRef.current) {
      !isSkeletonLoaderVisible && toggleSkeletonLoader(true);
      debouncedFilterAction(searchInputValue);
    }
  }, [cutoffTimeRange, filterRadioCapacity, filterRadioActiveInactiveStores]);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
    }
  });
  return (
    <>
      <div className="capacity-info__store-container">
        <div className="capacity-info__store-header">
          <h6>Store Capacity</h6>
          <InputComponent
            className="store-search-input"
            id="store-search"
            name="store-search"
            label="Search store"
            value={searchInputValue}
            size="small"
            onChange={onSearchInputChange}
            inputAdornment={memoInputAdornment}
          />
          <ButtonComponent
            className="csv-button"
            text="Download CSV"
            onClick={prepareAndDownloadCSV}
            endIcon={csvDownloadIcon}
          />
          <ButtonComponent
            className="filter-button"
            text="Filter"
            buttonRef={filterButtonRef}
            onClick={onOpenPopover}
            startIcon={filterIcon}
          />
        </div>
        <TableContainer className="capacity-info__store-table" component="div">
          <Table>
            <TableHead>
              <TableRow>
                {Object.keys(TableHeaders).map((key: string) => (
                  <TableCell
                    data-key={key}
                    align={key !== 'stores' ? 'center' : 'left'}
                    key={key}
                    onClick={setActiveSorting}
                  >
                    <span style={{ fontWeight: 700, fontStyle: 'Roboto' }}>
                      {' '}
                      {TableHeaders[key as TableHeadersTypes]}{' '}
                    </span>{' '}
                    {key !== 'stores' && key !== 'active' ? (
                      <span
                        style={{
                          fontStyle: 'italic',
                          fontSize: 10,
                          display: 'block',
                        }}
                      >
                        (Regular - Large)
                      </span>
                    ) : (
                      ''
                    )}
                    {sortObject.sortKey === key &&
                      (sortObject.sortType === 'asc' ? (
                        <FaSortAmountDownAlt data-key={key} />
                      ) : (
                        <FaSortAmountDown data-key={key} />
                      ))}
                  </TableCell>
                ))}
                {allowEditing && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {(filteredSortedStores || storesCapacity)
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((store: StoreCapacity, ix: number) => {
                  if (isSkeletonLoaderVisible) {
                    return (
                      <TableRow key={store.name}>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return (
                    <TableRow key={store.name}>
                      <TableCell>{store.name}</TableCell>
                      <TableCell align="center">
                        {someButNotAllZonesOpen(store) ? (
                          <TooltipComponent
                            id={`pickup-tooltip-${store.name}-${ix}`}
                            title={pickupToolTipData(store)}
                          >
                            {someZonesOpen(store) ? (
                              <span className="capacity-info__dot yellow" />
                            ) : (
                              ''
                            )}
                          </TooltipComponent>
                        ) : someZonesOpen(store) ? (
                          <span className="capacity-info__dot green" />
                        ) : (
                          <span className="capacity-info__dot red" />
                        )}
                      </TableCell>
                      {/*\xa0 means &nbsp;*/}
                      <TableCell align="center">
                        <span>{`R ${formatCutOff(
                          store
                        )} \xa0-\xa0 L ${qbbCutoffAdjustmentMinutes(
                          store
                        )}`}</span>
                      </TableCell>
                      <TableCell align="center">
                        {`R ${store.todaysOrderCount || 0} \xa0-\xa0 L ${
                          store.todaysLargeOrderCount
                        }`}
                      </TableCell>
                      <TableCell align="center">
                        {`R ${
                          store.maxOrdersForDate ?? String.fromCodePoint(0x221e)
                        } \xa0-\xa0 L ${store.defaultMaxDailyLargeOrders}`}
                      </TableCell>
                      {allowEditing && (
                        <TableCell align="center">
                          <ButtonComponent
                            startIcon={editIcon}
                            text="Edit"
                            variant="text"
                            value={store.name}
                            onClick={triggerStoreEdit}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          className="capacity-info__store-table-pagination"
          rowsPerPageOptions={[8, 16, 24]}
          count={(filteredSortedStores || storesCapacity)?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </div>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={isToastMessageVisible}
        autoHideDuration={3000}
        onClose={onCloseToast}
      >
        <Alert onClose={onCloseToast} severity={toastConfigObject.type}>
          {toastConfigObject.text}
        </Alert>
      </Snackbar>
      <CapacityStoreFilterPopover
        filterRadioCapacity={filterRadioCapacity}
        setFilterRadioCapacityCallback={setFilterRadioCapacityCallback}
        filterRadioActiveInactiveStores={filterRadioActiveInactiveStores}
        setFilterRadioActiveInactiveStoresCallback={
          setFilterRadioActiveInactiveStoresCallback
        }
        cutoffTimeVisualRange={cutoffTimeVisualRange}
        setCutoffTimeRangeCallback={setCutoffTimeRangeCallback}
        setCutoffVisualChangeCallback={setCutoffVisualChangeCallback}
        timeRangeValueText={timeRangeValueText}
        filterButtonRef={filterButtonRef}
        onClosePopover={onClosePopover}
        isPopoverVisible={isPopoverVisible}
        clearAllFilter={clearAllFilter}
      />
      {selectedStore && (
        <ModalComponent
          showCloseButton={true}
          className="edit-store-config-modal-window"
          open={isEditStoreConfigModalVisible}
          setOpen={toggleEditStoreModal}
          title={`Edit capacity for ${capitalize(selectedStore?.name || '')}`}
        >
          <EditStoreConfigModal
            storeName={selectedStore.name}
            accountId={selectedStore.accountId}
            pickupDays={selectedStore.pickupDays}
            dailyCutOffTime={formatCutOff(selectedStore, '')}
            onCloseModal={onCloseModal}
            onShowLarge={() => modalToggleLargeSection()}
            onShowDelivery={() => modalToggleDeliverySection()}
            onShowSpecial={() => modalToggleSpecialSection()}
            showLarge={showLarge}
            showDelivery={showDelivery}
            showSpecial={showSpecial}
            updateStoresCapacity={updateStoresCapacity}
            toggleLoader={toggleSkeletonLoader}
            toggleToastMessage={toggleToastMessage}
            setToastConfigObject={setToastConfigObject}
          />
        </ModalComponent>
      )}
    </>
  );
});

CapacityStoreSection.displayName = 'CapacityStoreSection';
CapacityStoreSection.whyDidYouRender = true;
export { CapacityStoreSection };
